import { BadgeCheck, MapPin, UserRound, CheckCircle2 } from "lucide-react";
import FadeUp from "@/components/landing/FadeUp";
import { SHORTLIST_PREVIEW } from "./content";
import { Card, SectionHeading } from "./Primitives";

const CandidateCard = ({ c, index }) => (
    <Card
        data-testid={`candidate-card-${index + 1}`}
        className="relative flex flex-col p-6 hover:border-brand-blue/40 hover:shadow-[0_24px_60px_-40px_rgba(45,91,255,0.45)] hover:-translate-y-0.5"
    >
        <div className="flex items-start justify-between gap-3">
            <div className="flex items-center gap-3 min-w-0">
                <div className="h-11 w-11 rounded-full bg-brand-blue-soft grid place-items-center text-brand-blue shrink-0">
                    <UserRound className="h-5 w-5" />
                </div>
                <div className="min-w-0">
                    <p className="text-[15px] font-semibold text-hire-ink leading-snug">{c.role}</p>
                    <p className="text-[12px] text-hire-muted">{c.level}</p>
                </div>
            </div>
            <span className="shrink-0 rounded-md bg-hire-bg px-2 py-1 text-[10.5px] font-semibold text-hire-muted tabular-nums">
                {c.ref}
            </span>
        </div>

        <div className="mt-4 flex flex-wrap gap-x-4 gap-y-1.5 text-[12.5px] text-hire-muted">
            <span className="inline-flex items-center gap-1.5"><MapPin className="h-3.5 w-3.5" /> {c.location}</span>
            <span className="inline-flex items-center gap-1.5">· {c.employment}</span>
        </div>

        <p className="mt-4 text-[13.5px] text-hire-ink/80 leading-relaxed flex-1">{c.summary}</p>

        <div className="mt-4 flex flex-wrap gap-1.5">
            {c.skills.map((s) => (
                <span key={s} className="rounded-md border border-hire-line bg-white px-2 py-0.5 text-[11.5px] font-medium text-hire-ink/80">
                    {s}
                </span>
            ))}
        </div>

        <div className="mt-5 pt-4 border-t border-hire-line">
            <div className="flex items-center justify-between">
                <p className="text-[11px] font-semibold tracking-[0.1em] uppercase text-hire-muted">Role fit</p>
                <p data-testid={`candidate-fit-${index + 1}`} className="text-sm font-bold text-brand-blue tabular-nums">{c.fit}%</p>
            </div>
            <div className="mt-2 h-1.5 w-full rounded-full bg-hire-bg overflow-hidden">
                <div className="h-full rounded-full bg-brand-blue" style={{ width: `${c.fit}%` }} />
            </div>
            <ul className="mt-4 grid grid-cols-2 gap-x-3 gap-y-1.5">
                {c.checks.map((k) => (
                    <li key={k} className="inline-flex items-center gap-1.5 text-[11.5px] text-[#047857] font-medium">
                        <CheckCircle2 className="h-3.5 w-3.5 shrink-0" /> {k}
                    </li>
                ))}
            </ul>
        </div>
    </Card>
);

export const ShortlistPreview = () => (
    <section id="shortlist-preview" data-testid="shortlist-preview-section" className="section-pad bg-hire-bg border-y border-hire-line">
        <div className="container-page">
            <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6">
                <SectionHeading
                    eyebrow={SHORTLIST_PREVIEW.eyebrow}
                    title={SHORTLIST_PREVIEW.heading}
                    sub={SHORTLIST_PREVIEW.sub}
                    testId="shortlist-preview-heading"
                />
                <FadeUp delay={0.1}>
                    <span className="inline-flex items-center gap-2 rounded-full border border-hire-line bg-white px-3 py-1.5 text-xs font-medium text-hire-muted whitespace-nowrap">
                        <BadgeCheck className="h-4 w-4 text-brand-blue" /> Sample shortlist · anonymized
                    </span>
                </FadeUp>
            </div>

            <div className="mt-12 md:mt-14 grid md:grid-cols-3 gap-5">
                {SHORTLIST_PREVIEW.cards.map((c, i) => (
                    <FadeUp key={c.ref} delay={i * 0.07}>
                        <CandidateCard c={c} index={i} />
                    </FadeUp>
                ))}
            </div>

            <FadeUp delay={0.1}>
                <p data-testid="shortlist-preview-note" className="mt-8 text-[13px] text-hire-muted">
                    {SHORTLIST_PREVIEW.note}
                </p>
            </FadeUp>
        </div>
    </section>
);

export default ShortlistPreview;
